<script setup lang="ts">
import {computed, ref} from 'vue';
import {useNotificationStore} from '@/stores/notifications';
import {Icon} from '@iconify/vue';
import {storeToRefs} from 'pinia';
import {useMusicUploadStore} from "@/stores/useMusicUploadStore.ts";
import {useSettingsStore} from '@/stores/settings';
import Collapsible from "@/components/generic/Collapsible.vue";
import {getDemoFile} from "@/stores/getDemoFile.ts";
import {dragHasFiles} from "@/shared/windowFileDrag";


// 'compact' blendet die Upload-Zone aus (Stepper nach erfolgreicher Analyse); beim Drag-over
// wird sie trotzdem gezeigt, damit ein Drop weiterhin möglich ist.
const {variant = 'full'} = defineProps<{ variant?: 'full' | 'compact' }>()
const emit = defineEmits<{

  (e: 'file-removed'): void;
}>();


const uploadStore = useMusicUploadStore();
const conversionStore = uploadStore

const {fileInfo, selectedFileContent, isPending, isConverting, isUploading, currentPdfObjectUrl} = storeToRefs(conversionStore);
const dragging = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
// Opt-in: PDF vor der OMR zu einem hochauflösenden, gesäuberten/verdickten Bild aufbereiten
// (hilft bei verrauschten Scans). Betrifft nur PDF-Uploads.
const improveImage = ref(true);
// Persistierte UI-Präferenz „Bass-Teil weglassen" (Default an). v-model schreibt in den
// Settings-Store zurück; der Upload-Store liest den Wert beim (PDF-)Import selbst.
const settingsStore = useSettingsStore();
const {removeBassParts} = storeToRefs(settingsStore);

type ReadableFileInfo = {
  name: string;
  size: string;
};

const readableFileInfo = computed<ReadableFileInfo | null>((oldValue) => {
  if (!fileInfo.value) {
    return null;
  }
  const {filename: name, file_size: size} = fileInfo.value;
  const formattedSize = `${((size ?? 1) / (1024 * 1024)).toFixed(2)} MB`;
  if (oldValue?.name === name && oldValue?.size === formattedSize) {
    return oldValue;
  }
  return {name, size: formattedSize};
});

function resetInput() {
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

function onDrop(event: DragEvent) {
  event.preventDefault();
  // Diese Karte verarbeitet den Drop selbst – nicht an einen übergeordneten Drop-Handler
  // durchreichen (sonst würde die Datei im Stepper doppelt geladen).
  event.stopPropagation();
  dragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length) {
    handleFile(files[0]);
  }
}

function onDragOver(event: DragEvent) {
  // Nur Datei-Drags blenden die Dropzone ein – Text-/Link-Drags sollen weder die Karte
  // (variant="compact" zeigt sie erst bei dragging) noch den Drop-Rahmen aktivieren.
  if (!dragHasFiles(event)) return;
  event.preventDefault();
  dragging.value = true;
}

function onDragLeave() {
  dragging.value = false;
}

function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    handleFile(file);
  }
  resetInput();
}

async function handleFile(file?: File) {
  try {
    if (file)
      await uploadStore.updateAndAnalyzeFile(file, improveImage.value);
  } catch (error: unknown) {
    console.error('File upload failed:', error);
    const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
    useNotificationStore().notify(`Fehler beim Hochladen der Datei: ${message}`, 'error', 4000);
  }

}

function removeFile() {
  uploadStore.reset();
  emit('file-removed');
}

const exampleFiles = [
  {name: 'Demo 1 – Alle meine Entchen', filename: 'demo.musicxml'},
  {name: 'Ennstaler - MusicXml', filename: 'Ennstaler.musicxml'},
  {name: 'Ennstaler - MIDI', filename: 'Ennstaler Polka.mid'},

  {name: 'Demo 3 – Piano', filename: 'demo-piano.musicxml'},
  {name: 'Demo 4 – Puchenberg from PDF', filename: 'Puchberg Polka-duo - Partitur-C.pdf'},
  {name: 'Demo 5 – Puchenberg from MusicXML', filename: 'puchenberg.musicxml'},
  {name: 'Demo 6 - Tonleiter C-Dur Sexten', filename: 'C-dur-tonleiter-sexten.musicxml'}
];

async function loadExampleFile(filename: string) {

  const exampleFile = await getDemoFile(filename);

  const upload = await uploadStore.updateAndAnalyzeFile(exampleFile, improveImage.value);
  useNotificationStore().notify(`Beispieldatei "${exampleFile.name}" hochgeladen.`, 'success', 2000);


}

</script>

<template>

  <div v-show="'full' === variant || dragging">
  <div class="bg-white rounded-lg shadow-md p-2 align-self-start" @drop="onDrop">
  <h2 class="text-lg font-semibold mb-4 flex justify-items-baseline ">
    <Icon icon="fa6-solid:file-arrow-up" class="text-blue-600 my-auto mr-2"/>
    <span class="h-full">
      Datei hochladen
      </span>
  </h2>

  <div
      id="dropzone"
      class="border-2 border-dashed rounded-lg text-center"
      :class="dragging ? 'border-sky-400 bg-sky-50' : 'border-gray-300 bg-white'"

      @dragover="onDragOver"
      @dragleave="onDragLeave"
  >
    <div
        @click="fileInput?.click()"
        class="p-2 flex items-center flex-col items-center justify-self-center   transition-colors cursor-pointer hover:border-blue-400 gap-2"
    >
      <div class="justify-self-center py-auto">
        <Icon icon="fa6-solid:cloud-arrow-up" class="text-4xl text-gray-400 "/>
      </div>
      <div class="text-gray-600 text-xs ">Datei hier ablegen oder klicken zum Auswählen</div>
      <div v-if="!selectedFileContent" class="text-md text-gray-500 text-italic">Unterstützte Formate: <b>.mxl .xml .musicxml .mid .midi .pdf</b></div>
      <div v-if="!selectedFileContent" class="text-xs text-gray-400 mt-1">Maximale Dateigröße: 10 MB</div>
    </div>

    <div class="px-3 pb-2 flex items-center justify-center">
      <label
          class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none"
          title="Für eingescannte (gedruckte) Notenhefte: rastert die PDF hochauflösend und verbessert die Notenlinien vor der automatischen Erkennung. Bei digitalen PDFs nicht empfohlen – kann das Ergebnis verschlechtern."
          @click.stop
      >
        <input
            type="checkbox"
            class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            v-model="improveImage"
        />
        <span>Scan-Qualität verbessern <span class="text-gray-400">(nur für eingescannte PDFs)</span></span>
      </label>
    </div>

    <div class="px-3 pb-2 flex items-center justify-center">
      <label
          class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none"
          title="Aktivieren, um beim PDF-Import Parts im Bass-Schlüssel zu entfernen. Standardmäßig bleibt der Bass erhalten (die Datei wird vollständig angezeigt). Die fertige Griffschrift ist bauartbedingt ohnehin einstimmig."
          @click.stop
      >
        <input
            type="checkbox"
            class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            v-model="removeBassParts"
        />
        <span>Bass-Teil entfernen <span class="text-gray-400">(nur bei PDF-Import)</span></span>
      </label>
    </div>

    <hr class="my-4 border-t-1 border-dashed border-blue-400/40" v-if="selectedFileContent"/>

    <Collapsible variant="plain" class="m-2" :title="readableFileInfo?'Beispiele':'wähle ein Beispiel aus'" is-open>
      <div class="flex flex-wrap gap-2">
        <button
            v-for="example in exampleFiles"
            :key="example.filename"
            :disabled="isPending"
            type="button"

            class="inline-flex items-center hover:bg-sky-100 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
            :class="{
              'bg-sky-50 text-sky-700 border-sky-200':!example.filename.endsWith('pdf')&& !example.filename.endsWith('mid') ,
              'text-orange-600 bg-orange-50 border-sky-200':example.filename.endsWith('pdf'),
              'text-green-600 bg-green-50 border-green-200':example.filename.endsWith('mid'),
              'bg-green-200 text-green-700 border-green-400 ring-2 ring-green-200':example.filename === fileInfo?.filename,

                 }"
            @click.stop="loadExampleFile(example.filename)"
        >
          <Icon :icon="example.filename === fileInfo?.filename ? 'fa6-solid:check' : (example.filename.endsWith('pdf')?'fa6-solid:file':'tabler:clef-staff')" class="mr-1.5"/>
          {{ example.name }}
        </button>
      </div>
    </Collapsible>

    <hr class="my-4 border-t-1 border-dashed border-blue-400/40" v-if="selectedFileContent"/>

  </div>
  </div>
  </div>
  <div v-if="readableFileInfo" class="relative mt-2 p-2 bg-green-50 rounded-lg">
    <div class="flex items-center justify-between">
      <div class="flex ">
        <Icon icon="fa6-solid:file-audio" class="text-green-600 mr-2"/>
        <div>
          <p class="font-medium text-gray-900">{{ readableFileInfo.name }}</p>
          <p class="text-sm text-gray-500">{{ readableFileInfo.size }}</p>
        </div>
      </div>
      <button class="text-red-500 hover:text-red-700" @click="removeFile">
        <Icon icon="fa6-solid:xmark"/>
      </button>
      <div v-if="isPending" class="absolute inset-0 z-50 bg-gray-100/40 backdrop-grayscale-50  ">

        <div class="text-center text-violet-600 bg-white/40  inset-0 backdrop-blur-lg  animate-rotate-x  animate-delay-8000 animate-once mx-auto py-2 z-50 text-sm font-semibold   ">
          <div class=" animate-rotate-y w-8 mx-auto animate-infinite animate-add animate-duration-4000 animate-linear animate-fill-forwards">
            <Icon icon="fa6-solid:spinner" class="animate-spin animate-infinite animate-duration-[2000ms] animate-ease-in-out animate-normal animate-add animate-fill-forwards shrink-0 scale-150 "/>
          </div>
          <div class="text-center mx-auto z-50 text-sm animate-pulse font-semibold max-w-max  mt-2">{{
              isUploading ? 'Upload läuft...' : 'Wird verarbeitet...'
            }}
          </div>
          <div v-if="currentPdfObjectUrl && isUploading" class="text-center mx-auto text-xs text-violet-500 mt-1 max-w-[220px]">
            PDF-Notenerkennung (OMR) läuft – das kann 30–120 Sekunden dauern.
          </div>
        </div>

      </div>

    </div>

  </div>
  <input
      ref="fileInput"
      type="file"
      id="fileInput"
      class="hidden inset-0"
      accept=".mxl,.xml,.musicxml,.mid,.midi,.pdf"
      @change="onFileChange"
  />
</template>
